/**
 * The state behind the per-message menu, owned once and used by both threads.
 *
 * The hearing and the doorman conversation are two different components with
 * two different composers and two different send paths, and they had better
 * behave identically here: the same gesture opens the same menu, copy says the
 * same word, and a quote lands above the composer the same way. A hook is how
 * that stays true without either file knowing about the other.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { withQuote, type Quote } from "../../lib/quote";
import type { MenuTarget } from "./bubble-menu";

/** How long "Copied." stands before the thread goes quiet again. */
const COPIED_MS = 1_600;

export type MessageActions = {
  menu: MenuTarget | null;
  /** The quote waiting above the composer, or null. */
  quote: Quote | null;
  /** Set once a copy lands, cleared on its own. Announced, never a toast. */
  copied: boolean;
  open: (target: MenuTarget) => void;
  close: () => void;
  copy: () => void;
  startQuote: () => void;
  clearQuote: () => void;
  /**
   * The message as it goes on the wire: the quote's prefix, then what was
   * typed. Clears the held quote, because a quote is spent by being sent.
   */
  compose: (body: string) => string;
};

export function useMessageActions(onFocusComposer?: () => void): MessageActions {
  const [menu, setMenu] = useState<MenuTarget | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [copied, setCopied] = useState(false);
  const timer = useRef(0);
  const focus = useRef(onFocusComposer);
  focus.current = onFocusComposer;

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const open = useCallback((target: MenuTarget) => setMenu(target), []);
  const close = useCallback(() => setMenu(null), []);

  const copy = useCallback(() => {
    const text = menu?.quote.text ?? "";
    setMenu(null);
    if (!text) return;
    // Best effort, and silent when it fails. A clipboard is denied by policy in
    // some browsers and there is nothing the sender can do about that; an error
    // in the thread would be the room complaining about its own furniture.
    void navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopied(true);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
      })
      .catch(() => undefined);
  }, [menu]);

  const startQuote = useCallback(() => {
    if (menu) setQuote(menu.quote);
    setMenu(null);
    focus.current?.();
  }, [menu]);

  const clearQuote = useCallback(() => setQuote(null), []);

  const compose = useCallback(
    (body: string) => {
      if (!quote) return body;
      setQuote(null);
      return withQuote(quote, body);
    },
    [quote],
  );

  return { menu, quote, copied, open, close, copy, startQuote, clearQuote, compose };
}
